import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ReportDataSource, ReportDraftState, ReportExportTooLargeError, ReportLifecycleState} from './data/ReportDataSource';
import {SavedReportRef} from './data/savedReportInput';
import {ReportRow} from './data/reportRowModel';
import {Condition, ReportDefinitionV1Tasks} from './contracts/report-definition.types';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {defaultOperatorForDataType, operatorForValues} from './conditions/conditionOperators';
import {DatePresetToken, findRelativeDatePreset} from './relativeDatePresets';
import {DateScope, resolveDatePeriod, withPeriodFilterConditions, periodFromConditions} from './periodFilter';
import {ReportTableState} from './ReportTable/ReportTable';
import {GroupingToken} from './ReportTable/useClientGrouping';
import {
	EXPANDER_COLUMN_ID,
	FAVORITE_COLUMN_ID,
	FIXED_COLUMN_IDS,
	ensureFieldPath,
	flattenFieldPaths,
	orderedDataColumnIds,
} from './ReportTable/useReportTableColumns';
import {useViewerLayoutOverlay} from './useViewerLayoutOverlay';

export type BuilderMode = 'edit' | 'view';
export type BuilderRole = 'editor' | 'reader';

/** What `publish()` resolves with. 'unconfirmed' is deliberately neither success nor failure: the
 * bounded wait expired, so the publish may still be in flight server-side. */
export type PublishOutcome = 'published' | 'locked' | 'failed' | 'unconfirmed';
/** What `discard()` resolves with. 'reload_failed' means the draft WAS dropped but the reverted
 * definition could not be read back; 'unconfirmed' means the bounded wait expired, so whether
 * anything was dropped is unknown. */
export type DiscardOutcome = 'discarded' | 'locked' | 'failed' | 'reload_failed' | 'unconfirmed';

/** What `exportCsv()` resolves with. 'too_large' is separated from 'failed' because report-service
 * refuses an over-ceiling ordered export rather than truncating it, and the same request will be
 * refused again — so the surface has to say to narrow the report, not to try again. */
export type ExportOutcome = 'exported' | 'too_large' | 'failed';

export interface ReportIntentPayload {
	reportType?: string;
	suggestedName?: string;
	definition: ReportDefinitionV1Tasks;
	summaries?: unknown[];
	flags?: unknown[];
	draftId?: string | null;
	// Original natural-language prompt the report was generated from. Rendered
	// (quoted, italic) in the AI banner. Two producers: the agent populates it on the
	// create_report_intent event for a fresh draft, and re-opening a saved report populates it from
	// the stored prompt. Optional either way; when absent the banner falls back to the static
	// "Generated from your prompt".
	prompt?: string;
	// Which surface authored the report. Absent on the fresh-intent path, where the builder is the
	// only possible author.
	source?: 'AI' | 'STANDARD';
	// Lifecycle state the report arrived with. Present on the re-open path (read from the stored
	// definition), absent on the fresh-intent path, where nothing is saved yet.
	draftState?: ReportDraftState;
	publishedAt?: string | null;
	// Whether the draft this report arrived with (if any) would revert the filter on discard. Same
	// two-path reasoning as `draftState`/`publishedAt`: present on the re-open path, absent on the
	// fresh-intent path, where there is no draft to describe.
	draftFilterWillRevert?: boolean;
	// Whether the report has already been hand-edited since it was generated from its prompt. Present
	// on the re-open path (read from the stored definition), absent on the fresh-intent path, where
	// nothing has been edited yet.
	manuallyEdited?: boolean;
}

export interface ReportBuilderContextValue {
	definition: ReportDefinitionV1Tasks; // working draft
	prompt?: string; // original NL prompt (AI banner); undefined when the payload omits it
	// Which surface authored the report, resolved. Drives the provenance banner: a report the builder
	// generated says so, a report opened from anywhere else does not.
	source: 'AI' | 'STANDARD';
	// Whether the report has been hand-edited since it was generated from its prompt, combining what
	// storage reported with anything edited this session. Drives the AI banner's second state.
	manuallyEdited: boolean;
	mode: BuilderMode;
	role: BuilderRole;
	dirty: boolean;
	tableState: ReportTableState;
	grouping: GroupingToken[];
	taxonomy: ReportTaxonomy | null;
	setMode(m: BuilderMode): void;
	setName(name: string): void;
	// The in-progress rename text, staged separately from `definition.name` until it is committed.
	// Null while the rename field is closed; `ReportPageHeader` opens it by seeding this from
	// `definition.name` and clears it (without committing) on revert/Escape.
	pendingName: string | null;
	setPendingName(name: string | null): void;
	// Applies the trimmed pending name via `setName` when it is non-empty and different from the
	// stored name (an unchanged name must not mark the report dirty), then closes the field either
	// way. A blank or whitespace-only pending name is discarded rather than committed — falling back
	// to the stored name, because a blank one would fail the `nameValid` guard that gates Publish.
	// Returns the name now in effect, so a caller that commits and immediately needs the current name
	// (the publish toast, an export filename) is not stuck reading this render's stale `definition`
	// while React catches up. Safe to call at any time, including with nothing staged (a no-op that
	// returns `definition.name`) — `save()` and `exportCsv()` call it themselves so a caller of either
	// never has to remember to, and `ReportPageHeader`'s own submit/Enter path goes through this exact
	// function rather than re-implementing the same rule.
	commitPendingName(): string;
	// The reporting period, as the date-range control shows it. Null members mean that end is open.
	// datePreset is the relative token the period was resolved from, or null for a custom/absolute
	// range set via the date picker directly. dateScope is which date field(s) the range filters on;
	// null means `all_dates` - the default, and what every report saved before the scope selector
	// existed means.
	period: {
		startDate: string | null;
		endDate: string | null;
		datePreset: DatePresetToken | null;
		dateScope: DateScope | null;
	};
	// Replaces the period with an absolute range and clears any selected preset. Rewrites the
	// definition's date filter, so the table re-runs for the new window; an open end drops that side
	// of the filter rather than filtering on a missing date.
	setPeriod(startDate: string | null, endDate: string | null): void;
	// Resolves a relative preset against the current date and replaces the period with the result,
	// recording the token so the period is recomputed fresh on every future load.
	setPeriodPreset(token: DatePresetToken): void;
	// Changes which date field(s) the period filters on, keeping the range itself unchanged. Rewrites
	// the definition's date filter to match the new scope.
	setPeriodScope(scope: DateScope): void;
	setGrouping(g: GroupingToken[]): void;
	updateTableState(patch: Partial<ReportTableState>): void;
	// Serialises layout/sort/grouping into the definition and persists it into the Reports list via
	// `dataSource.saveDefinition` — creating the report on the first save and updating the same
	// report on every save after it. Resolves `true` when the report is saved and `false` when the
	// save failed; it never rejects, so a caller that forgets to handle failure gets a no-op rather
	// than an unhandled rejection that tears the surface down. A failed save leaves the draft dirty
	// and editable, which keeps Save enabled as the standing "not saved yet" signal.
	save(): Promise<boolean>;
	// Id of the Reports-list entry this draft has been saved as, or null while it is unsaved. Set by
	// the first successful `save()` and reused to update that entry rather than create duplicates.
	// Deliberately NOT the signal for "does this report exist in storage" — a stored report whose
	// sharing flag came back unrecognised is seeded with no `savedReportSeed` (see
	// `ReportBuilderPage`) so a save cannot guess and flip its privacy, which leaves this null even
	// though the report is genuinely saved. Use `openedFromStorage` for that question instead.
	savedReportId: string | null;
	// Whether the builder was opened on a report that already exists in storage (the route carried a
	// report id), as opposed to a fresh chat-generated draft that exists only in this session. Unlike
	// `savedReportId`, this stays true regardless of whether the stored record's sharing flag was
	// readable, because it answers a different question: not "can a save target this record without
	// guessing," but "would leaving right now destroy something with no copy anywhere else."
	openedFromStorage: boolean;
	// Records a privacy change made outside `save()` — the share dialog's "share with company" toggle
	// commits straight to the backend, so without this the next `save()` would echo the stale value it
	// mounted with (see `buildUpdateSavedReportInput`) and silently re-privatize a report the user just
	// shared. A no-op while there is no saved report yet: an unsaved draft cannot be shared, so the
	// dialog is unreachable there.
	updateSavedReportPrivateAccess(privateAccess: boolean): void;
	// True while a save is in flight. The surface must not allow a second save while set: a repeated
	// first save would create a second Reports-list entry for the same draft.
	saving: boolean;
	// Exports the current definition/grouping's full (unpaginated) result set as a CSV via
	// `dataSource.exportCsv`. The definition sent is the draft with the live table state folded in (the
	// same serialisation `save()` performs, without persisting it), so the CSV's column order and row
	// order match the table on screen even when the session's reorder/resize/sort has not been saved.
	// Resolves `'exported'` when the file was handed to the browser, `'too_large'` when the report has
	// more rows than the export will order, and `'failed'` for anything else. `'too_large'` is its own
	// outcome because it is the one failure retrying cannot fix, so the surface must not offer that as
	// the remedy. Never rejects, mirroring `save()`'s contract for the same reason — a caller that
	// forgets to handle failure gets a no-op rather than an unhandled rejection that tears the surface
	// down.
	exportCsv(): Promise<ExportOutcome>;
	// True while an export is in flight. The surface must not allow a second export while set: a
	// repeated click would start a second full-dataset export against the same definition.
	exporting: boolean;
	// Whether the report has a usable name. The name an unsaved draft arrives with is the agent's
	// suggestion, which is an acceptable name to save under; only a blank one blocks Save, because a
	// nameless entry in the Reports list cannot be identified or reopened.
	nameValid: boolean;
	toggleFavorite(rowId: string): void; // optimistically flips the row, persists via the data source, reverts on failure
	addCondition(fieldKey: string): void; // no-op when taxonomy is null or fieldKey is unknown
	// Adds a taxonomy column to the definition's `fields`, which is what makes it a real column:
	// fetched, rendered, listed in ColumnManager and saved. No-op when taxonomy is null, when
	// fieldKey is unknown, or when the column is already there.
	addColumn(fieldKey: string): void;
	// `operator` is only supplied by a control that writes the whole comparison itself (Remaining
	// work's Yes/No). Every other picker omits it, and the operator keeps being inferred from how
	// many values were picked.
	updateConditionValues(index: number, values: string[], operator?: string): void;
	removeCondition(index: number): void;
	rows: ReportRow[];
	totalCount: number;
	capped: boolean;
	cappedTotalEstimate: number;
	totalIsFloor: boolean;
	// False until the first fetch for the current definition resolves. Lets the surface tell
	// "not fetched yet" apart from "fetched, zero matches" so the empty state never claims the
	// report ran while the initial fetch is still in flight.
	hasFetched: boolean;
	// True when the fetch rejected (non-2xx or network failure). Kept separate from `hasFetched`
	// so the surface can show an error instead of the empty state — the empty state must only ever
	// claim "the report ran and matched nothing" after a real success.
	fetchFailed: boolean;
	// Re-runs the fetch for the current definition. The fetch is otherwise only triggered by a
	// definition change, so without this a transient network failure would be a dead end.
	retryFetch(): void;
	// Where the report sits in the publish lifecycle. 'UNPUBLISHED' until a lifecycle call or a load
	// says otherwise, e.g. a fresh AI-generated draft that has never been saved.
	draftState: ReportDraftState;
	publishedAt: string | null;
	// Whether discarding the currently open draft would also revert the report's filter. False
	// whenever there is no open draft, or the draft predates this field. Drives whether the discard
	// confirmation copy warns about the filter.
	draftFilterWillRevert: boolean;
	// True while a publish is in flight, mirroring `saving`'s role for the reentrancy check and for
	// the surface to disable the Publish action.
	publishing: boolean;
	// True while a discard is in flight, same reasoning as `publishing`.
	discarding: boolean;
	// True when what is on screen is no longer known to match the stored report, so nothing on screen is
	// safe to publish. Set when a discard dropped the draft but its re-read failed (the definition on
	// screen is the copy that was just discarded), and when a lifecycle call's bounded wait expired (the
	// call may still land server-side, so what is stored is unknown). The surface must gate Publish and
	// Discard on this and say so, rather than offering to publish content it cannot vouch for, and must
	// tell the editor to reload the page — that is the recovery, because only a fresh load can be trusted
	// to show what is stored. Cleared only by a re-read that succeeds within the same session (the
	// discard's own re-read). Deliberately NOT cleared by an edit: an edit on top of a stale base is
	// still stale.
	reloadRequired: boolean;
	// True when a publish got as far as saving the working copy but never landed the publish itself (the
	// publish call failed, or another user held the edit lock). The draft is genuinely saved, so `dirty`
	// is false and `draftState` is unchanged, which would otherwise leave the Publish action disabled
	// with no way back other than making an unrelated edit. The surface must include this in whatever
	// enables Publish. Cleared by the next successful publish, and by a discard, which throws the
	// working copy away so there is nothing left to publish.
	publishOwed: boolean;
	// Opens the edit session before handing the surface over to edit mode. Never rejects: a draft call
	// that fails still enters edit mode, because the alternative is an editor stuck in view mode. A draft
	// call whose bounded wait expired enters edit mode with `reloadRequired` set, because whether a draft
	// is open is then unknown.
	beginEdit(): Promise<void>;
	// Saves the working copy and makes it the version readers execute. Resolves 'published' on
	// success, 'locked' when another user holds the edit lock, 'failed' for a failed save or publish,
	// and 'unconfirmed' when the call did not settle inside `LIFECYCLE_TIMEOUT_MS` — which is neither
	// success nor failure, because the request may still land server-side. Never rejects, for the same
	// reason `save()` does not.
	publish(): Promise<PublishOutcome>;
	// Drops the edit session and adopts the definition report-service reverted to. Resolves
	// 'discarded', 'locked', 'failed' (nothing was discarded, nothing changed), 'reload_failed' (the
	// draft WAS discarded, but the reverted definition could not be read back, so what is on screen is
	// stale) or 'unconfirmed' (the call did not settle in time, so whether anything was discarded is
	// unknown). Never rejects.
	discard(): Promise<DiscardOutcome>;
}

const ReportBuilderContext = createContext<ReportBuilderContextValue | null>(null);

/** Collapse window for definition-driven refetches. Long enough that ticking several values in a
 * condition's picker issues one request instead of one per click, short enough that a single edit still
 * feels immediate next to the request's own latency. */
const FETCH_DEBOUNCE_MS = 400;

/** Bound on how long any single lifecycle round trip is waited on: entering a draft, publishing,
 * discarding, and the definition re-reads that follow a discard.
 *
 * What it respects: the slowest legitimate round trip. Each of those calls is one short report-service
 * transaction (publish writes a snapshot row for the working copy, discard clears the draft pointer,
 * entering a draft records the session), reached through the graphql hop, so even a loaded environment
 * with a cold connection pool settles in low single-digit seconds. The bound is set an order of
 * magnitude above that rather than close to it on purpose.
 *
 * What exceeding it costs: the editor is told the outcome could not be confirmed and has to reload the
 * page to find out where it landed — so a merely slow but successful publish must never trip it, which
 * is why the headroom is generous. The bound exists because without one a request that never settles (a
 * hung request, or a dropped connection with no browser-level timeout) latches the shared in-flight guard
 * for the rest of the session: Publish, Discard and the edit-session call are all refused from then on,
 * with the buttons disabled and no explanation. */
export const LIFECYCLE_TIMEOUT_MS = 30000;

/** A bounded wait's result: the settled value, or the fact that the wait expired first. */
type BoundedResult<T> = {timedOut: true} | {timedOut: false; value: T};

/** Races `work` against `LIFECYCLE_TIMEOUT_MS`. A rejection propagates, so callers keep handling
 * transport failures in their own catch; expiry resolves `{timedOut: true}` instead, because a call
 * that has not settled is not a call that failed. The pending work is given its own no-op catch: after
 * an expiry nothing awaits it any more, and a late rejection would otherwise surface as an unhandled
 * rejection. The timer is always cleared, so an expired-or-settled call leaves no pending timeout
 * behind. */
function withLifecycleTimeout<T>(work: Promise<T>): Promise<BoundedResult<T>> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const settled: Promise<BoundedResult<T>> = work.then(value => ({timedOut: false as const, value}));
	settled.catch(() => undefined);
	const expiry = new Promise<BoundedResult<T>>(resolve => {
		timer = setTimeout(() => resolve({timedOut: true}), LIFECYCLE_TIMEOUT_MS);
	});
	return Promise.race([settled, expiry]).finally(() => {
		if (timer !== undefined) {
			clearTimeout(timer);
		}
	});
}

/** The period the date control should show. Prefers the definition's own dates and falls back to the
 * date filter, so a report saved before the period was editable - or scoped to a single own-date
 * field rather than all dates - still shows the window it filters on. */
function periodOf(definition: ReportDefinitionV1Tasks): {
	startDate: string | null;
	endDate: string | null;
	datePreset: DatePresetToken | null;
	dateScope: DateScope | null;
} {
	// The conditions are the authoritative window: they are what the fetch actually filters on, and
	// `buildSaveReportAsInput`/`buildUpdateSavedReportInput` derive the stored startDate/endDate from
	// them, so the two agree for anything this builder wrote. The raw fields still win when present,
	// because a report can legitimately carry a range with no conditions yet.
	const fromConditions = periodFromConditions(definition.filter.filterConditions);
	return {
		startDate: definition.startDate ?? fromConditions.startDate,
		endDate: definition.endDate ?? fromConditions.endDate,
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
	};
}

/** Canonical `columnOrder` derived from the definition alone (no overlay/table-state input):
 * layout order when present, else field-tree order, wrapped by the fixed expander/favourite
 * columns — reuses `useReportTableColumns`'s `orderedDataColumnIds` so the initial table state
 * matches what the columns hook will actually render (expander leading, favourite column
 * trailing — see `useReportTableColumns`). */
function canonicalColumnOrder(definition: ReportDefinitionV1Tasks): string[] {
	return [EXPANDER_COLUMN_ID, ...orderedDataColumnIds(definition), FAVORITE_COLUMN_ID];
}

function canonicalColumnSizing(definition: ReportDefinitionV1Tasks): Record<string, number> {
	const sizing: Record<string, number> = {};
	for (const entry of definition.layout ?? []) {
		// A width that was never set is absent on an agent-generated definition but null on one read back
		// from a saved report (the stored column width is nullable), so both have to be treated as unset —
		// a null here would reach the table as a null column size.
		if (typeof entry.width === 'number') {
			sizing[entry.field] = entry.width;
		}
	}
	return sizing;
}

function canonicalSorting(definition: ReportDefinitionV1Tasks): ReportTableState['sorting'] {
	if (!definition.sort) {
		return [];
	}
	return [{id: definition.sort.field, desc: definition.sort.direction === 'DESC'}];
}

/** Whether two table states describe the same arrangement. Compared by value rather than identity
 * because the working state is rebuilt on every interaction, so an identity check would report a
 * difference the moment anything was touched, even if it was put straight back. */
function isSameTableState(a: ReportTableState, b: ReportTableState): boolean {
	return (
		JSON.stringify(a.sorting) === JSON.stringify(b.sorting) &&
		JSON.stringify(a.columnOrder) === JSON.stringify(b.columnOrder) &&
		JSON.stringify(a.columnSizing) === JSON.stringify(b.columnSizing) &&
		JSON.stringify(a.columnVisibility) === JSON.stringify(b.columnVisibility)
	);
}

function canonicalTableState(definition: ReportDefinitionV1Tasks): ReportTableState {
	return {
		sorting: canonicalSorting(definition),
		columnOrder: canonicalColumnOrder(definition),
		columnSizing: canonicalColumnSizing(definition),
		columnVisibility: {},
	};
}

/** The data-column ids the user has switched off in ColumnManager. `columnVisibility` records only the
 * columns that have been toggled, and only `false` means hidden, so an absent entry is visible. */
function hiddenColumnPaths(tableState: ReportTableState): string[] {
	return Object.entries(tableState.columnVisibility)
		.filter(([, visible]) => visible === false)
		.map(([columnId]) => columnId);
}

/** Serializes the current `tableState`/`grouping` into a NEW definition object (never mutates
 * `base`), per spec §6 save() rules: `layout` is `columnOrder` filtered of fixed (`__`-prefixed)
 * and hidden columns, paired with `columnSizing` when set; `sort` is the primary sort column, if
 * any; `grouping` is copied verbatim.
 *
 * Dropping hidden columns here does NOT persist their hidden-ness: layout carries order and width, and a
 * re-opened report rebuilds its column set from the field tree, so a hidden column comes back visible.
 * Hiding is session state until the definition contract gains a visibility concept. */
function serializeDefinition(
	base: ReportDefinitionV1Tasks,
	tableState: ReportTableState,
	grouping: GroupingToken[]
): ReportDefinitionV1Tasks {
	const layout = tableState.columnOrder
		.filter(id => !id.startsWith('__'))
		.filter(id => tableState.columnVisibility[id] !== false)
		.map(id => {
			const width = tableState.columnSizing[id];
			return width !== undefined ? {field: id, width} : {field: id};
		});

	const primarySort = tableState.sorting[0];
	const sort = primarySort
		? {field: primarySort.id, direction: (primarySort.desc ? 'DESC' : 'ASC') as 'ASC' | 'DESC'}
		: undefined;

	return {
		...base,
		layout,
		sort,
		grouping: grouping.slice() as ReportDefinitionV1Tasks['grouping'],
	};
}

export interface ReportBuilderProviderProps {
	payload: ReportIntentPayload;
	role?: BuilderRole;
	// Required: callers pass the transport explicitly (production: HttpReportDataSource via
	// ReportBuilderPage; tests/Storybook: a mock). No implicit default, so a mock can never be the
	// silent production fallback.
	dataSource: ReportDataSource;
	// Scopes the per-user localStorage layout overlay to the logged-in person. The page passes the
	// viewer's person id from the app's auth context; falls back to 'me' only when the id is unknown
	// (e.g. tests/Storybook that don't supply one).
	userId?: string;
	// Field metadata driving addCondition/updateConditionValues; null until the taxonomy for this
	// report type has loaded. addCondition no-ops while null.
	taxonomy?: ReportTaxonomy | null;
	/** The Reports-list entry this report already exists as, when the builder was opened on a saved
	 * report rather than a fresh draft. Without it the provider treats a saved report as a new draft
	 * and a save creates a duplicate entry instead of updating the one being edited. */
	savedReportSeed?: SavedReportRef;
	/** Whether the builder was opened on a report that already exists in storage (the route carried a
	 * report id), independent of whether `savedReportSeed` could be populated. Passed through as
	 * `openedFromStorage` on the context; see that field for why it is kept separate from
	 * `savedReportId`. Defaults to false: the only other way to reach the builder is the chat intent,
	 * which never has a stored record yet. */
	openedFromStorage?: boolean;
	/** Announces the report id the first time this builder turns its payload into a stored report.
	 * Fired once, on the transition from unsaved to saved — never on subsequent updates, where the id
	 * has not changed. Lets whatever proposed this report (today: a chat card) point at the stored
	 * report from then on instead of re-opening its own increasingly stale payload. Absent when the
	 * builder was opened on a report that already exists. */
	onIntentSaved?: (savedReportId: string) => void;
	children: React.ReactNode;
}

/** Recursively flips `favorite` on the row matching `rowId` (and leaves every other row, including
 * siblings/children, referentially unchanged) so React only re-renders the affected row/ancestors. */
function toggleFavoriteInRows(rows: ReportRow[], rowId: string): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			changed = true;
			return {...row, favorite: !row.favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = toggleFavoriteInRows(row.children, rowId);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

/** Sets `favorite` on the row matching `rowId` to an explicit value (rather than flipping its
 * current value), so a rollback restores exactly the pre-optimistic-update state even if `rows` was
 * replaced by an intervening refetch in the meantime — flipping the row's CURRENT value would
 * silently write the wrong state in that case, since "current" may no longer be what the optimistic
 * update produced. Leaves every other row, including siblings/children, referentially unchanged. */
function setFavoriteInRows(rows: ReportRow[], rowId: string, favorite: boolean): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			if (row.favorite === favorite) {
				return row;
			}
			changed = true;
			return {...row, favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = setFavoriteInRows(row.children, rowId, favorite);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

/** Finds a row by id anywhere in the tree (including nested children), or null if absent. */
function findRowById(rows: ReportRow[], rowId: string): ReportRow | null {
	for (const row of rows) {
		if (row.id === rowId) {
			return row;
		}
		if (row.children.length > 0) {
			const found = findRowById(row.children, rowId);
			if (found) {
				return found;
			}
		}
	}
	return null;
}

/** A fresh chat-drafted intent states its period via `datePreset`/`startDate`/`endDate` plus
 * `dateScope` alone - the agent that authors it is forbidden from emitting the period's filter
 * conditions itself. The saved-report reload path (`toReportDefinition`) already derives them
 * before this component ever sees the definition; a fresh intent has had no such step, so without
 * this it would fetch completely unfiltered until the first save and reload. Reuses the exact
 * derivation the reload path applies, via `resolveDatePeriod`, so the two paths cannot silently
 * diverge from each other. */
function withDerivedIntentPeriod(definition: ReportDefinitionV1Tasks): ReportDefinitionV1Tasks {
	const resolved = resolveDatePeriod(definition, definition.filter.filterConditions, 'always');
	return {
		...definition,
		datePreset: resolved.datePreset,
		startDate: resolved.startDate,
		endDate: resolved.endDate,
		filter: {...definition.filter, filterConditions: resolved.filterConditions},
	};
}

export function ReportBuilderProvider({
	payload,
	role = 'editor',
	dataSource,
	userId = 'me',
	taxonomy = null,
	savedReportSeed,
	openedFromStorage = false,
	onIntentSaved,
	children,
}: ReportBuilderProviderProps): JSX.Element {
	const resolvedDataSource = dataSource;
	const reportId = payload.draftId ?? 'draft';

	// Only a fresh intent needs the period derived here: a report opened from storage arrives
	// already derived (see `toReportDefinition`), and re-deriving it a second time on top of an
	// already-consistent definition is needless work on every saved-report open.
	const [definition, setDefinition] = useState<ReportDefinitionV1Tasks>(() =>
		openedFromStorage ? payload.definition : withDerivedIntentPeriod(payload.definition)
	);
	// The rename field's staged text; null while it is closed. See `commitPendingName` below for how
	// it turns into `definition.name`.
	const [pendingName, setPendingName] = useState<string | null>(null);
	// A reader has no edit mode at all. An editor's landing mode depends on which path opened the
	// builder, and `savedReportSeed` is the signal: it is only supplied when the builder was opened on a
	// report that already exists in the Reports list.
	//
	// A re-opened saved report lands in VIEW mode, so "Edit report" is the only way in. That button is
	// what calls `beginEdit`, and `beginEdit` is what opens a draft — mounting straight into edit mode
	// would let an editor change columns with no draft open, so every save would land on the version
	// readers execute, defeating the isolation the draft exists to provide.
	//
	// A fresh draft has no seed and lands in EDIT mode: it does not exist in the Reports list yet, so
	// there is no published version to isolate readers from and nothing to view — the editor is still
	// building it.
	const [mode, setMode] = useState<BuilderMode>(role === 'reader' || savedReportSeed !== undefined ? 'view' : 'edit');
	const [dirty, setDirty] = useState(false);
	const [grouping, setGroupingState] = useState<GroupingToken[]>((payload.definition.grouping ?? []) as GroupingToken[]);
	const [rows, setRows] = useState<ReportRow[]>([]);
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	const [totalCount, setTotalCount] = useState(0);
	const [capped, setCapped] = useState(false);
	const [cappedTotalEstimate, setCappedTotalEstimate] = useState(0);
	const [totalIsFloor, setTotalIsFloor] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [fetchFailed, setFetchFailed] = useState(false);
	// Seeded from `savedReportSeed` so a re-opened report updates the entry it already has instead of
	// creating a duplicate on the next save (and so `beginEdit`/`discard` have a report id to act on).
	// Null on the fresh-intent path, where the first save creates the entry.
	const [savedReport, setSavedReport] = useState<SavedReportRef | null>(savedReportSeed ?? null);
	const [saving, setSaving] = useState(false);
	// Mirrors `saving` for the reentrancy check in `save()`, which cannot wait for a render.
	const savingRef = useRef(false);
	const [exporting, setExporting] = useState(false);
	// Mirrors `exporting` for the reentrancy check in `exportCsv()`, which cannot wait for a render.
	const exportingRef = useRef(false);
	// The saved-report id as of the last successful save, readable inside the same call that saved —
	// `savedReport` state is not visible until the next render.
	const savedReportIdRef = useRef<string | null>(savedReportSeed?.id ?? null);
	// Held in a ref so a caller passing an inline arrow does not re-create `save` on every render.
	const onIntentSavedRef = useRef(onIntentSaved);
	onIntentSavedRef.current = onIntentSaved;
	// Bumped by `retryFetch` to re-run the fetch effect without changing the definition.
	const [fetchAttempt, setFetchAttempt] = useState(0);
	const [draftState, setDraftState] = useState<ReportDraftState>(payload.draftState ?? 'UNPUBLISHED');
	const [publishedAt, setPublishedAt] = useState<string | null>(payload.publishedAt ?? null);
	const [draftFilterWillRevert, setDraftFilterWillRevert] = useState<boolean>(payload.draftFilterWillRevert ?? false);
	// Two values, not one, so `discard()` can drop a flag raised inside the discarded draft without
	// lowering one the report already carried in from an earlier session. A single boolean cannot
	// express both: clearing it would lose a real prior edit, keeping it would preserve an edit the
	// user just threw away. The stored half is state rather than a derived constant because a discard
	// re-reads the report and the server's answer supersedes what the session mounted with.
	const [manuallyEditedStored, setManuallyEditedStored] = useState(payload.manuallyEdited === true);
	const [manuallyEditedSession, setManuallyEditedSession] = useState(false);
	const manuallyEdited = manuallyEditedStored || manuallyEditedSession;
	const [publishing, setPublishing] = useState(false);
	const [discarding, setDiscarding] = useState(false);
	const [publishOwed, setPublishOwed] = useState(false);
	const [reloadRequired, setReloadRequired] = useState(false);
	// Generation of the current lifecycle call, in the spirit of `latestDefinitionRef` below: bumped
	// whenever a call is abandoned because its bounded wait expired. Read by `save()` alone, which is the
	// one call the shared in-flight guard below does not cover (`publish()` awaits it, so folding it in
	// would deadlock): a save whose generation has moved on has been superseded, because the publish that
	// wrapped it gave up waiting, the editor was told the outcome was unknown, and the editor may have
	// edited since. Every other lifecycle call is serialised by the shared guard, so nothing can bump the
	// generation while one of them awaits.
	const lifecycleGenerationRef = useRef(0);
	/** Abandons the current lifecycle call: any result that arrives from now on is superseded. */
	const supersedeLifecycleCall = useCallback(() => {
		lifecycleGenerationRef.current += 1;
	}, []);
	// Shared in-flight guard across ALL THREE lifecycle calls (beginEdit/publish/discard), set
	// synchronously for the same reason as `savingRef` — state is not readable in the same tick.
	// Without a shared guard a discard could run while a publish is still in flight (or vice versa);
	// both write draftState/publishedAt and discard also swaps the definition, so whichever response
	// lands last would win regardless of which action the user meant last. Deliberately separate from
	// `savingRef`: `publish()` calls `save()` internally, and `save()` guards itself independently, so
	// folding `save()` into this guard would make `publish()` deadlock against its own save call.
	const lifecycleInFlightRef = useRef(false);

	const validColumnIds = useMemo(() => {
		const ids = new Set(flattenFieldPaths(definition.fields));
		FIXED_COLUMN_IDS.forEach(id => ids.add(id));
		return ids;
	}, [definition.fields]);

	const {overlay, write: writeOverlay} = useViewerLayoutOverlay(reportId, userId, validColumnIds);

	const canonical = useMemo(() => canonicalTableState(definition), [definition]);

	// In-memory table state for the current session. Initialised from canonical + (for readers)
	// the persisted overlay on top; editors' in-memory edits flow through `save()` instead of the
	// overlay. Once a session has interacted with the table this local state is authoritative —
	// we intentionally do not re-derive it from `canonical`/`overlay` on every render (that would
	// stomp in-flight edits), only on definition identity change (see reset below).
	const initialTableState = useMemo<ReportTableState>(() => {
		if (role !== 'reader' || !overlay) {
			return canonical;
		}
		return {...canonical, ...overlay};
	}, [canonical]);

	const [tableState, setTableState] = useState<ReportTableState>(initialTableState);

	// Fetches on mount and whenever `definition` identity changes (e.g. after `save()`), plus on
	// demand via `retryFetch`. There is no loading spinner, so this stays fire-and-forget; a stale
	// in-flight response from a superseded definition is guarded against via the ref check.
	//
	// Edits are debounced, because a definition edit is not a discrete event: ticking values in a
	// condition's value picker produces a new definition per click, and running the whole report on each
	// one put dozens of fetches on the wire in a couple of seconds. The mount fetch is NOT debounced —
	// there is nothing to collapse it with, and delaying it would leave the surface blank for no reason.
	// `hasFetched` flips to true once the first response lands, so the empty state can wait for a
	// real (possibly zero-row) result rather than rendering during the initial in-flight window.
	// A rejection (non-2xx or network failure) is caught and surfaced as `fetchFailed` — leaving it
	// unhandled would both render a bare empty grid and raise an unhandled promise rejection.
	const latestDefinitionRef = useRef(definition);
	latestDefinitionRef.current = definition;
	// False until the mount fetch has been issued, which is one of the runs that must not be delayed.
	const hasScheduledFetchRef = useRef(false);
	// The `fetchAttempt` this effect last ran for, so an explicit retry can be told apart from an edit.
	const lastFetchAttemptRef = useRef(fetchAttempt);

	useEffect(() => {
		let cancelled = false;
		const isStale = (): boolean => cancelled || latestDefinitionRef.current !== definition;
		setFetchFailed(false);
		const runFetch = (): void => {
			if (cancelled) {
				return;
			}
			void resolvedDataSource.fetchRows(definition, {offset: 0, limit: 1000}).then(
				result => {
					if (isStale()) {
						return;
					}
					setRows(result.rows);
					setTotalCount(result.totalCount);
					setCapped(result.capped);
					setCappedTotalEstimate(result.cappedTotalEstimate);
					setTotalIsFloor(result.totalIsFloor);
					setHasFetched(true);
				},
				() => {
					if (isStale()) {
						return;
					}
					// Clear any previous result so a stale table can't linger under the error.
					setRows([]);
					setTotalCount(0);
					setCapped(false);
					setCappedTotalEstimate(0);
					setTotalIsFloor(false);
					setHasFetched(false);
					setFetchFailed(true);
				}
			);
		};

		// Only an edit-driven rerun is debounced. The mount fetch has nothing to collapse with, and a
		// retry is a deliberate click on "try again" — delaying either just makes the surface look stuck.
		const isRetry = fetchAttempt !== lastFetchAttemptRef.current;
		lastFetchAttemptRef.current = fetchAttempt;
		if (!hasScheduledFetchRef.current || isRetry) {
			hasScheduledFetchRef.current = true;
			runFetch();
			return () => {
				cancelled = true;
			};
		}

		const timer = setTimeout(runFetch, FETCH_DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [definition, resolvedDataSource, fetchAttempt]);

	const retryFetch = useCallback(() => {
		setFetchAttempt(n => n + 1);
	}, []);

	const updateSavedReportPrivateAccess = useCallback((privateAccess: boolean) => {
		setSavedReport(prev => (prev ? {...prev, privateAccess} : prev));
	}, []);

	const setName = useCallback((name: string) => {
		setDefinition(prev => ({...prev, name}));
		setDirty(true);
	}, []);

	/** See the interface doc above. This is the one place the staged-name rule lives — trim, compare,
	 * apply-or-fall-back, always close — so `ReportPageHeader`'s submit/Enter path and `save`/`exportCsv`
	 * (which call this themselves) can never disagree about what a commit does. */
	const commitPendingName = useCallback((): string => {
		if (pendingName === null) {
			return definition.name;
		}
		const trimmed = pendingName.trim();
		setPendingName(null);
		if (trimmed === '' || trimmed === definition.name) {
			return definition.name;
		}
		setName(trimmed);
		return trimmed;
	}, [pendingName, definition.name, setName]);

	/** Tasks are matched by OVERLAP with the period, not containment: a task counts when it starts on or
	 * before the period ends and finishes on or after the period begins. Filtering for tasks fully inside
	 * the window would hide exactly the long-running work a period report is usually about. Each side is
	 * independent, so an open start or end simply drops its condition. */
	const setPeriod = useCallback((startDate: string | null, endDate: string | null) => {
		setDefinition(prev => ({
			...prev,
			startDate: startDate ?? undefined,
			endDate: endDate ?? undefined,
			datePreset: undefined,
			filter: {
				...prev.filter,
				filterConditions: withPeriodFilterConditions(prev.filter.filterConditions, startDate, endDate, prev.dateScope),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const setPeriodPreset = useCallback((token: DatePresetToken) => {
		const preset = findRelativeDatePreset(token);
		if (!preset) {
			return;
		}
		const {startDate, endDate} = preset.resolve(new Date());
		setDefinition(prev => ({
			...prev,
			startDate,
			endDate,
			datePreset: token,
			filter: {
				...prev.filter,
				filterConditions: withPeriodFilterConditions(prev.filter.filterConditions, startDate, endDate, prev.dateScope),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	/** Changes which date field(s) the period filters on, keeping the range itself. Re-derives the
	 * conditions rather than editing them: withPeriodFilterConditions clears the outgoing scope's
	 * fields, so switching scope cannot leave two incompatible date windows ANDed together. */
	const setPeriodScope = useCallback((scope: DateScope) => {
		setDefinition(prev => {
			// The EFFECTIVE period, via the same derivation the date control displays - not the raw
			// `startDate`/`endDate` fields. A definition whose window lives only in its filter
			// conditions carries no top-level dates, so reading those directly rebuilds the conditions
			// from an empty range: every date condition is dropped and none replaces it, leaving the
			// report unfiltered while the control still shows the window it was filtering on.
			// Resolving through the fallback also promotes that window onto the definition, so nothing
			// downstream has to re-derive it.
			const {startDate, endDate} = periodOf(prev);
			return {
				...prev,
				startDate: startDate ?? undefined,
				endDate: endDate ?? undefined,
				dateScope: scope,
				filter: {
					...prev.filter,
					filterConditions: withPeriodFilterConditions(prev.filter.filterConditions, startDate, endDate, scope),
				},
			};
		});
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const setGrouping = useCallback((g: GroupingToken[]) => {
		setGroupingState(g);
		setDirty(true);
	}, []);

	/** Sorting, resizing and reordering are the only table mutations reachable in view mode — the
	 * toolbar that carries every other control is not rendered there. For an editor they are a
	 * viewing gesture, not an edit to the report, so they must not set `dirty`: that would arm the
	 * unsaved-changes pill and the leave-without-saving overlay against someone who only looked.
	 *
	 * An editor's view-mode rearrangement is therefore in-memory for the session only. It is
	 * deliberately NOT written to the viewer overlay: that overlay is read back on mount for readers
	 * alone, so writing it here would persist a layout nothing ever restores. */
	const updateTableState = useCallback(
		(patch: Partial<ReportTableState>) => {
			setTableState(prev => ({...prev, ...patch}));
			if (role === 'reader') {
				writeOverlay(patch);
			} else if (mode === 'edit') {
				setDirty(true);
			}
		},
		[role, mode, writeOverlay]
	);

	const save = useCallback(async () => {
		// Reentrancy guard, and deliberately a ref rather than the `saving` state: the state only gates
		// the Save button after a render has committed, so two calls raised before that commit would both
		// see `savedReport` as null, both take the create branch, and leave two Reports-list entries for
		// one draft with only the later one tracked. A ref is set synchronously, so the second call is
		// refused whatever the render timing.
		if (savingRef.current) {
			return false;
		}
		savingRef.current = true;
		const generation = lifecycleGenerationRef.current;
		// Commits an open, uncommitted rename before anything is serialised: without this an editor who
		// typed a new name and hit Publish/Save without pressing the tick would have it silently
		// discarded — the field was staged local state nobody read here. `commitPendingName` resolves
		// synchronously, so the override below (rather than reading `definition.name`) is what makes the
		// just-committed name available immediately, before this render's `definition` closure catches up.
		const resolvedName = commitPendingName();
		const nextDefinition = serializeDefinition({...definition, name: resolvedName}, tableState, grouping);
		setSaving(true);
		try {
			const ref = await resolvedDataSource.saveDefinition(
				nextDefinition,
				savedReport,
				payload.prompt ?? null,
				manuallyEdited
			);
			// Recorded even when this save has been superseded: the id is the report's identity, never
			// something the user can have changed in the meantime, and dropping it would let a later save
			// take the create branch and leave two Reports-list entries for one draft.
			setSavedReport(ref);
			// Read before the assignment below overwrites it: this is the only save that turns the
			// payload into a report with an id, and the only one anybody upstream needs to hear about.
			const wasUnsaved = savedReportIdRef.current === null;
			savedReportIdRef.current = ref.id;
			if (wasUnsaved) {
				onIntentSavedRef.current?.(ref.id);
			}
			if (generation !== lifecycleGenerationRef.current) {
				// The one generation check that can fire, and the reason the counter exists: `save()` is the
				// only call the shared lifecycle guard does not cover, so a `publish()` that gave up waiting on
				// this very save has already told the editor the outcome could not be confirmed, and the editor
				// may have edited since. The save itself did land, so say so, but do not adopt the definition it
				// saved over whatever is on screen now. Every other lifecycle call holds that guard for its whole
				// run, so no generation can move while one of them awaits.
				return true;
			}
			// Adopting the serialised definition also re-runs the fetch (the effect keys on definition
			// identity), so what is on screen after a save is the saved definition run against live
			// data — the same thing re-opening the report from the Reports list will show.
			setDefinition(nextDefinition);
			setDirty(false);
			return true;
		} catch {
			// Deliberately swallowed. The draft is left dirty and editable so Save stays enabled and the
			// user can retry; re-throwing would surface as an unhandled rejection in any caller that
			// does not chain a catch, which is what tore the surface down before.
			return false;
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	}, [definition, tableState, grouping, resolvedDataSource, savedReport, payload.prompt, manuallyEdited, commitPendingName]);

	const exportCsv = useCallback(async (): Promise<ExportOutcome> => {
		// Reentrancy guard, and deliberately a ref rather than the `exporting` state — same reasoning
		// as `save()`'s guard: two calls raised before a render commits would both see `exporting` as
		// false and both start a full-dataset export. A ref is set synchronously, so the second call is
		// refused whatever the render timing.
		//
		// Reported as 'failed' rather than as a distinct outcome: the first export is still running and
		// will report its own, and the busy control already says why this press did nothing.
		if (exportingRef.current) {
			return 'failed';
		}
		exportingRef.current = true;
		setExporting(true);
		try {
			// Commits an open, uncommitted rename first, for the same reason `save()` does: the exported
			// file's name comes from this definition, and an editor who typed a new name and clicked
			// Download without pressing the tick must get a file named for what is on screen, not the
			// last-committed name.
			const resolvedName = commitPendingName();
			// Serialised, not the raw working draft: column order, column widths and the sort column live
			// in `tableState` until a save folds them into the definition, so exporting `definition`
			// directly would hand the backend the last-saved (or agent-generated) layout and no sort at
			// all — a CSV whose columns and rows are ordered differently from the table the user is
			// looking at. `serializeDefinition` returns a new object, so the draft itself stays untouched
			// and an export does not count as a save.
			// Hidden columns travel separately, not folded into the serialised definition: `layout`
			// records order and width only, and `orderedDataColumnIds` deliberately re-appends any leaf
			// field the layout omits so an agent-generated subset still renders - which would put a
			// column the user just hid back into the file.
			await resolvedDataSource.exportCsv(
				serializeDefinition({...definition, name: resolvedName}, tableState, grouping),
				grouping,
				hiddenColumnPaths(tableState)
			);
			return 'exported';
		} catch (error) {
			// Deliberately swallowed, mirroring `save()`: re-throwing would surface as an unhandled
			// rejection in any caller that does not chain a catch, which is what tore the surface down
			// before. The one failure worth distinguishing is the row ceiling, which no retry can get past.
			return error instanceof ReportExportTooLargeError ? 'too_large' : 'failed';
		} finally {
			exportingRef.current = false;
			setExporting(false);
		}
	}, [definition, tableState, grouping, resolvedDataSource, commitPendingName]);

	/** Opens the edit session before handing the surface over to edit mode. A report that has never
	 * been saved has no report row to draft, so it goes straight to edit; a draft call that fails
	 * still enters edit mode, because the alternative is an editor stuck in view mode. No-ops (stays
	 * wherever it was) when another lifecycle call is already in flight, rather than racing it.
	 *
	 * A draft call that never settles is neither the success nor the failure path: the draft may have opened
	 * server-side, so the wait is bounded, the guard is released, and the surface is marked out of step
	 * instead of picking a lifecycle state. Falling back to PUBLISHED/UNPUBLISHED there would have the
	 * banner assert "there is no draft open", which can be false, and would leave Discard disabled on a
	 * draft that may well exist. Out of step says only what is true: the editor is in edit mode, nothing
	 * about the stored report can be confirmed, and the page has to be reloaded. */
	const beginEdit = useCallback(async () => {
		if (lifecycleInFlightRef.current) {
			return;
		}
		// Sorting, resizing and reordering in view mode is a viewing gesture and deliberately does not
		// mark the report changed. But the table state it produces is the state a later save writes, so
		// entering edit mode with the table arranged differently from the stored report means the next
		// publish would carry that arrangement. Say so on the way in: the editor sees the unsaved-changes
		// pill and can discard, rather than publishing a rearrangement nothing ever told them about.
		if (!isSameTableState(tableState, canonicalTableState(definition))) {
			setDirty(true);
		}
		lifecycleInFlightRef.current = true;
		try {
			const reportId = savedReport?.id;
			if (!reportId) {
				setMode('edit');
				return;
			}
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.enterDraft(reportId));
				if (bounded.timedOut) {
					supersedeLifecycleCall();
					setReloadRequired(true);
					return;
				}
				const state = bounded.value;
				setDraftState(state.draftState ?? 'UNPUBLISHED');
				setPublishedAt(state.publishedAt);
				setDraftFilterWillRevert(state.draftFilterWillRevert);
			} catch {
				// Failing to open a draft means no draft was opened — the report's lifecycle state is
				// unchanged. A known publish date means it is still published; only fall back to
				// `UNPUBLISHED` when there is no publish date to fall back to. Never `DRAFT`: the
				// surface must not claim reader isolation it did not get.
				setDraftState(publishedAt !== null ? 'PUBLISHED' : 'UNPUBLISHED');
			} finally {
				setMode('edit');
			}
		} finally {
			lifecycleInFlightRef.current = false;
		}
		// `tableState`/`definition` are dependencies because the check above reads them: without them this
		// callback keeps the arrangement as it was when edit mode was last available, and a rearrangement
		// made since would go unflagged.
	}, [resolvedDataSource, savedReport, publishedAt, supersedeLifecycleCall, tableState, definition]);

	/** Saves the working copy and then makes it the version readers execute. Resolves 'published' on
	 * success, 'locked' when another user holds the edit lock, 'failed' for a failed save or publish, and
	 * 'unconfirmed' when either leg's bounded wait expired — which claims nothing either way, because the
	 * request may still land. Never rejects, for the same reason `save()` does not. */
	const publish = useCallback(async (): Promise<PublishOutcome> => {
		if (lifecycleInFlightRef.current) {
			return 'failed';
		}
		if (savingRef.current) {
			// A save is still in flight, and `save()` refuses a second one, so this publish would save nothing
			// and could only report a failure that never happened. The in-flight save may still land, so the
			// stored report cannot be spoken for: gate the surface rather than telling the editor to try again.
			// Reached only if a save is triggered outside a publish, since a publish holds the guard above for
			// its whole run.
			setReloadRequired(true);
			return 'unconfirmed';
		}
		lifecycleInFlightRef.current = true;
		setPublishing(true);
		try {
			// The save is bounded too: `publish()` awaits it, so a save that never settles would latch this
			// call's guard exactly as a hung publish would. Superseding it also stops the save from adopting
			// its definition once it finally lands.
			const boundedSave = await withLifecycleTimeout(save());
			if (boundedSave.timedOut) {
				supersedeLifecycleCall();
				setReloadRequired(true);
				return 'unconfirmed';
			}
			if (!boundedSave.value) {
				// Nothing was saved, so nothing is owed: `save()` leaves the draft dirty, which keeps Publish
				// enabled on its own.
				return 'failed';
			}
			const reportId = savedReportIdRef.current;
			if (!reportId) {
				setPublishOwed(true);
				return 'failed';
			}
			const bounded = await withLifecycleTimeout(resolvedDataSource.publish(reportId));
			if (bounded.timedOut) {
				// The working copy is saved and the publish may or may not have landed, so neither the debt nor
				// the lifecycle state can be stated: gate the surface and let the editor reload the page to find out.
				supersedeLifecycleCall();
				setReloadRequired(true);
				return 'unconfirmed';
			}
			const state = bounded.value;
			if (state.locked) {
				// The save landed but the publish did not, and the save cleared `dirty` — record the debt so the
				// retry the toast invites is not blocked by a disabled Publish button.
				setPublishOwed(true);
				return 'locked';
			}
			setDraftState(state.draftState ?? 'PUBLISHED');
			setPublishedAt(state.publishedAt);
			setDraftFilterWillRevert(state.draftFilterWillRevert);
			setPublishOwed(false);
			return 'published';
		} catch {
			// Reached only after `save()` resolved (it never rejects), so the working copy is saved and only
			// the publish is missing.
			setPublishOwed(true);
			return 'failed';
		} finally {
			lifecycleInFlightRef.current = false;
			setPublishing(false);
		}
	}, [resolvedDataSource, save, supersedeLifecycleCall]);

	/** Drops the edit session and adopts the definition report-service reverted to, so what is on
	 * screen always matches what was stored.
	 *
	 * The discard and the follow-up re-read are two separate failures with two separate truths. A failed
	 * discard changed nothing, server-side or here. A failed re-read means the draft IS gone — readers are
	 * already back on the live working copy — and only the reverted definition is missing, so the
	 * lifecycle state the discard reported is applied BEFORE the re-read is attempted. Applying it
	 * afterwards left the banner promising reader isolation that no longer existed, on top of telling the
	 * user nothing had been discarded.
	 *
	 * Resolves 'discarded', 'locked', 'failed', 'reload_failed' or 'unconfirmed'; never rejects. */
	const discard = useCallback(async (): Promise<DiscardOutcome> => {
		const reportId = savedReport?.id;
		if (!reportId || lifecycleInFlightRef.current) {
			return 'failed';
		}
		lifecycleInFlightRef.current = true;
		setDiscarding(true);
		try {
			let state: ReportLifecycleState;
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.discard(reportId));
				if (bounded.timedOut) {
					// Whether the draft was dropped is unknown, so neither the lifecycle state nor the definition
					// on screen can be trusted from here on: gate the surface and let the editor reload the page.
					supersedeLifecycleCall();
					setReloadRequired(true);
					return 'unconfirmed';
				}
				state = bounded.value;
			} catch {
				// No draft was dropped, so nothing here changes either.
				return 'failed';
			}
			if (state.locked) {
				return 'locked';
			}
			setDraftState(state.draftState ?? 'PUBLISHED');
			setPublishedAt(state.publishedAt);
			setDraftFilterWillRevert(state.draftFilterWillRevert);
			// The working copy the publish was owed for is gone, so the debt is gone with it.
			setPublishOwed(false);
			// The draft WAS just dropped, so an open, uncommitted rename goes with it too — discarding
			// everything else while quietly applying whatever was mid-edit in the name field would be
			// exactly the silent-apply-on-blur behaviour the rename design deliberately has no path for.
			setPendingName(null);
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.loadDefinition(reportId));
				if (bounded.timedOut) {
					supersedeLifecycleCall();
					// The draft IS gone and the reverted definition never arrived, which is the `reload_failed`
					// situation reached by a re-read that hung rather than failed.
					setDirty(false);
					setReloadRequired(true);
					return 'reload_failed';
				}
				const reverted = bounded.value;
				// The stored sharing flag is adopted along with the definition: `buildUpdateSavedReportInput`
				// sends `privateAccess` on every update and report-service overwrites it unconditionally, so the
				// next save has to echo the value this re-read just proved rather than the one the session
				// started with, or it would silently flip the report between private and shared. A null carries
				// no truthful value, so the known one is kept.
				const revertedPrivateAccess = reverted.privateAccess;
				if (typeof revertedPrivateAccess === 'boolean') {
					setSavedReport(prev => (prev === null ? prev : {...prev, privateAccess: revertedPrivateAccess}));
				}
				setDefinition(reverted.definition);
				setGroupingState((reverted.definition.grouping ?? []) as GroupingToken[]);
				// Re-derive tableState from the reverted definition too — otherwise column order/sizing/sort
				// stay whatever the editor left them as pre-discard, so the table would keep showing a layout
				// the adopted definition no longer describes.
				setTableState(canonicalTableState(reverted.definition));
				setDirty(false);
				// Adopted from the re-read rather than assumed: the server owns this flag, and a discard is
				// the one path that can lower it, because it throws away the edits that raised it. Clearing
				// only the session half and trusting a stale mount value let the banner disagree with
				// storage until the next reload.
				setManuallyEditedStored(reverted.manuallyEdited === true);
				setManuallyEditedSession(false);
				setReloadRequired(false);
			} catch {
				// What is on screen is the pre-discard working copy, which report-service has already dropped.
				// `dirty` is cleared and the surface is gated: leaving `dirty` set kept Publish enabled, and
				// publishing from here would put back exactly the content the editor was just told was
				// discarded. Neither Publish nor Discard means anything until the page is reloaded.
				setDirty(false);
				setReloadRequired(true);
				return 'reload_failed';
			}
			return 'discarded';
		} finally {
			lifecycleInFlightRef.current = false;
			setDiscarding(false);
		}
	}, [resolvedDataSource, savedReport, supersedeLifecycleCall]);

	// Row ids with a persist call in flight. Guards against a rapid double-click firing two
	// overlapping mutations for the same row — the second click is ignored outright (not queued)
	// while the first is pending, since a queued second call's rollback would revert whatever the
	// first call's outcome left behind rather than its own prior state.
	const pendingFavoriteRowIdsRef = useRef<Set<string>>(new Set());

	const toggleFavorite = useCallback(
		(rowId: string) => {
			if (pendingFavoriteRowIdsRef.current.has(rowId)) {
				return;
			}
			const row = findRowById(rowsRef.current, rowId);
			// A row that no longer exists locally, a deliverable row (which has no backing task and
			// cannot be favourited), or a task/subtask row with no taskId (should not happen once the
			// server always forces it — defensive), gets zero effect. The UI never renders a favorite
			// toggle for a deliverable row (see useReportTableColumns.tsx), but guard here too since this
			// callback is reachable programmatically via the context value, not just through that
			// button's onClick.
			if (!row || row.type === 'deliverable' || !row.taskId) {
				return;
			}
			const taskId = row.taskId;
			const wasFavourite = row.favorite;
			const nextFavoured = !wasFavourite;
			pendingFavoriteRowIdsRef.current.add(rowId);
			setRows(prev => toggleFavoriteInRows(prev, rowId));
			resolvedDataSource
				.toggleFavorite(taskId, nextFavoured)
				.catch(() => {
					// Low-stakes, instantly-retryable action (unlike Save/Publish): revert the optimistic
					// flip and stop there. No toast — the reverted star is sufficient feedback, and the
					// user can just click it again. Restores the captured pre-click value explicitly
					// (rather than re-flipping the row's current value): an intervening refetch (e.g. an
					// edited filter) can replace `rows` with fresh server data while this mutation is
					// still in flight, so "current" may already differ from what the optimistic update
					// produced — flipping it again would then write the wrong state.
					setRows(prev => setFavoriteInRows(prev, rowId, wasFavourite));
				})
				.finally(() => {
					pendingFavoriteRowIdsRef.current.delete(rowId);
				});
		},
		[resolvedDataSource]
	);

	const addCondition = useCallback(
		(fieldKey: string) => {
			if (!taxonomy) {
				return;
			}
			const field = fieldByKey(taxonomy, fieldKey);
			// Skip a field with no `filterField`: a condition stores the filterField enum, so a null
			// one would create a `field: null` condition that crashes label/summary rendering. Such
			// fields (non-filterable derived booleans) are never offered by the editor, but guard here
			// too so no caller can inject one.
			// Bound to a local so the narrowing survives into the `setDefinition` updater below; a
			// property access narrowed on `field` does not carry into a closure.
			const filterField = field?.filterField;
			if (!field || !filterField) {
				return;
			}
			setDefinition(prev => ({
				...prev,
				filter: {
					...prev.filter,
					filterConditions: [
						...prev.filter.filterConditions,
						{
							// Conditions store the filterField enum (STATUS, PROJECT, ...), not the
							// taxonomy key the picker is keyed by; the editor passes the key, we store
							// the field's filterField so it round-trips through the report-service contract.
							field: filterField,
							operator: defaultOperatorForDataType() as Condition['operator'],
							values: [],
							context: field.context as Condition['context'],
						},
					],
				},
			}));
			setDirty(true);
			setManuallyEditedSession(true);
		},
		[taxonomy]
	);

	const addColumn = useCallback(
		(fieldKey: string) => {
			// Same guard as addCondition, for the same reason: the definition may only reference
			// fields report-service actually serves. `fields` is what the fetch requests verbatim, so
			// an unknown key would come back with no cell and render an N/A column forever.
			if (!taxonomy || !fieldByKey(taxonomy, fieldKey)) {
				return;
			}
			// ensureFieldPath returns the same array when the path is already present, which makes
			// re-adding a column a genuine no-op rather than a state change that marks the report
			// dirty and triggers a pointless refetch. Decided here, off the ref that tracks the
			// current definition, so the `setDefinition` updater below stays a pure function of its
			// argument — React may run an updater more than once, so no side effect belongs inside it.
			if (ensureFieldPath(latestDefinitionRef.current.fields, fieldKey) === latestDefinitionRef.current.fields) {
				return;
			}
			setDefinition(prev => {
				const fields = ensureFieldPath(prev.fields, fieldKey);
				if (fields === prev.fields) {
					return prev;
				}
				// `fields` is a non-empty tuple on the wire type (the schema's minItems: 1), which
				// ensureFieldPath's plain array return cannot prove. It is non-empty by construction:
				// prev.fields already was, and ensureFieldPath only ever adds. Same cast idiom
				// serializeDefinition uses for `grouping`.
				return {...prev, fields: fields as ReportDefinitionV1Tasks['fields']};
			});
			// `columnOrder` is not derived from the definition after mount, so a field added here has
			// to be put into it explicitly. Left out, the table falls back to TanStack's "columns the
			// order does not mention go last", which puts the new column to the RIGHT of the fixed
			// favourite column; dragging it does nothing (`computeReorderedColumns` cannot find an id
			// the order lacks); and `serializeDefinition` builds `layout` from `columnOrder`, so a
			// save drops the column's position and width. Insert it where the canonical order would
			// have it: after the data columns, before the trailing favourite column.
			setTableState(prev => {
				if (prev.columnOrder.includes(fieldKey)) {
					return prev;
				}
				const favoriteIndex = prev.columnOrder.indexOf(FAVORITE_COLUMN_ID);
				const insertAt = favoriteIndex === -1 ? prev.columnOrder.length : favoriteIndex;
				const columnOrder = [...prev.columnOrder];
				columnOrder.splice(insertAt, 0, fieldKey);
				return {...prev, columnOrder};
			});
			setDirty(true);
		},
		[taxonomy]
	);

	const updateConditionValues = useCallback((index: number, values: string[], operator?: string) => {
		setDefinition(prev => {
			const filterConditions = prev.filter.filterConditions.map((condition, i) =>
				i === index
					? {
							...condition,
							values,
							// An explicit operator comes from a control that owns the whole comparison
							// (Remaining work's Yes/No). Otherwise keep inferring it from how many values
							// were picked, which is what every id-backed picker relies on.
							operator: (operator ?? operatorForValues(condition.operator, values)) as Condition['operator'],
					  }
					: condition
			);
			return {...prev, filter: {...prev.filter, filterConditions}};
		});
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const removeCondition = useCallback((index: number) => {
		setDefinition(prev => ({
			...prev,
			filter: {
				...prev.filter,
				filterConditions: prev.filter.filterConditions.filter((_, i) => i !== index),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const value: ReportBuilderContextValue = {
		definition,
		prompt: payload.prompt,
		// The only way to reach the builder without a stored record is the chat intent, which the
		// builder itself produced, so an absent value defaults to 'AI'. The re-open path always
		// supplies an explicit value.
		source: payload.source ?? 'AI',
		manuallyEdited,
		mode,
		role,
		dirty,
		tableState,
		grouping,
		taxonomy,
		setMode,
		setName,
		pendingName,
		setPendingName,
		commitPendingName,
		period: periodOf(definition),
		setPeriod,
		setPeriodPreset,
		setPeriodScope,
		setGrouping,
		updateTableState,
		save,
		savedReportId: savedReport?.id ?? null,
		openedFromStorage,
		updateSavedReportPrivateAccess,
		saving,
		exportCsv,
		exporting,
		nameValid: definition.name.trim() !== '',
		toggleFavorite,
		addCondition,
		addColumn,
		updateConditionValues,
		removeCondition,
		rows,
		totalCount,
		capped,
		cappedTotalEstimate,
		totalIsFloor,
		hasFetched,
		fetchFailed,
		retryFetch,
		draftState,
		publishedAt,
		draftFilterWillRevert,
		publishing,
		discarding,
		publishOwed,
		reloadRequired,
		beginEdit,
		publish,
		discard,
	};

	return <ReportBuilderContext.Provider value={value}>{children}</ReportBuilderContext.Provider>;
}

export function useReportBuilder(): ReportBuilderContextValue {
	const ctx = useContext(ReportBuilderContext);
	if (!ctx) {
		throw new Error('useReportBuilder must be used within a ReportBuilderProvider');
	}
	return ctx;
}
